import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  inject,
  OnInit,
  ViewChild,
  DestroyRef,
} from '@angular/core';
import { SessionService } from '@seech/auth-ng';
import {
  LayoutNgModule,
  MenuItem,
  MobileNavComponent,
  NavItem,
  SubsiteHubModalComponent,
} from '@seech-sub/layout-ng';
import { ModalService } from '@seech/ux-ng';
import { environment } from '../../environments/environment';
import {
  OperationsModulePermissions,
  QuotaPermissions,
  OperationsRoutes,
} from '../constants/operations.constants';
import {
  OperationsSessionResponse,
  OperationsUserSessionResponse,
} from '../models';
import {
  OperationsAccessService,
  SecuredNavItem,
} from '../security/services/operations-access.service';
import { AccountClientService } from '../clients/account-client.service';

@Component({
  standalone: true,
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  imports: [LayoutNgModule],
})
export class LayoutComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly sessionService = inject(
    SessionService<OperationsUserSessionResponse, OperationsSessionResponse>,
  );
  private readonly operationsAccessService = inject(OperationsAccessService);
  private readonly accounts = inject(AccountClientService);
  private readonly modal = inject(ModalService);

  launchHub(): void {
    this.modal.open(SubsiteHubModalComponent, {
      data: { source: this.accounts.getHub() },
      modalClass: 'modal-lg modal-dialog-centered modal-dialog-scrollable',
    });
  }

  @ViewChild('mobileNav') mobileNav!: MobileNavComponent;

  showFooter = true;
  sidebarCollapsed = false;
  sidebarWidth = '20%';
  user = {} as OperationsUserSessionResponse;
  logoLink: string = OperationsRoutes.dashboard;

  private readonly securedNavItems: SecuredNavItem[][] = [
    [
      {
        label: 'Dashboard',
        link: OperationsRoutes.dashboard,
        icon: 'dashboard-rounded',
        permissions: OperationsModulePermissions.dashboard,
      },
      {
        label: 'Integrations',
        link: OperationsRoutes.integrations,
        icon: 'stacks-fill',
        permissions: OperationsModulePermissions.integrations,
      },
      {
        label: 'Apps',
        link: OperationsRoutes.apps,
        icon: 'secured',
        permissions: OperationsModulePermissions.apps,
      },
      {
        label: 'Feedback',
        link: OperationsRoutes.feedback,
        icon: 'chat',
        permissions: OperationsModulePermissions.feedback,
      },
      {
        label: 'Legal',
        link: OperationsRoutes.legal,
        icon: 'clipboard',
        permissions: OperationsModulePermissions.legal,
      },
      {
        label: 'Feature flags',
        link: OperationsRoutes.featureFlags,
        icon: 'toggle-off',
        permissions: OperationsModulePermissions.featureFlags,
      },
      {
        label: 'Messages',
        link: OperationsRoutes.messageTemplates,
        icon: 'mail',
        permissions: OperationsModulePermissions.messageTemplates,
      },
      {
        label: 'Observability',
        link: OperationsRoutes.observability,
        icon: 'search',
        permissions: OperationsModulePermissions.observability,
      },
      {
        label: 'Alerts',
        link: OperationsRoutes.alertRecipients,
        icon: 'mail',
        permissions: OperationsModulePermissions.alertRecipients,
      },
      {
        label: 'Affiliates',
        link: OperationsRoutes.affiliates,
        icon: 'group',
        permissions: OperationsModulePermissions.affiliates,
      },
      {
        label: 'Merchants',
        link: OperationsRoutes.merchants,
        icon: 'briefcase',
        permissions: OperationsModulePermissions.merchants,
      },
      {
        label: 'Quotas & Billing',
        link: OperationsRoutes.quotas,
        icon: 'wallet',
        permissions: QuotaPermissions,
      },
    ],
    [
      {
        label: 'Users & Access',
        link: OperationsRoutes.usersAccess,
        icon: 'secured',
        permissions: OperationsModulePermissions.usersAccess,
      },
      {
        label: 'Audit',
        link: OperationsRoutes.audit,
        icon: 'search',
        permissions: OperationsModulePermissions.audit,
      },
      {
        label: 'Approvals',
        link: OperationsRoutes.approvals,
        icon: 'note-approve',
        permissions: OperationsModulePermissions.approvals,
      },
    ],
  ];

  navItems: NavItem[][] = [];

  headerMenuItems: MenuItem[] = [
    {
      label: 'Account',
      hint: 'Profile and personal details',
      icon: 'account-circle',
      routerLink: environment.ACCOUNT_WEB_URL,
      external: true,
      group: 'account',
    },
    {
      label: 'Security',
      hint: 'Password, sessions and devices',
      icon: 'secured',
      routerLink: `${environment.ACCOUNT_WEB_URL}/security`,
      external: true,
      group: 'account',
    },
    {
      label: 'Payments',
      hint: 'Wallet, transactions and methods',
      icon: 'wallet',
      routerLink: environment.PAYMENT_WEB_URL,
      external: true,
      group: 'apps',
    },
    {
      label: 'Log out',
      hint: 'End this session on this device',
      icon: 'logout',
      routerLink: `/${OperationsRoutes.logout}`,
      tone: 'danger',
      group: 'session',
    },
  ];

  private readonly securedMobileNavItems: SecuredNavItem[] = [
    {
      label: 'Dashboard',
      icon: 'dashboard-rounded',
      link: OperationsRoutes.dashboard,
      permissions: OperationsModulePermissions.dashboard,
    },
    {
      label: 'Integrations',
      icon: 'stacks-fill',
      link: OperationsRoutes.integrations,
      permissions: OperationsModulePermissions.integrations,
    },
    {
      label: 'Apps',
      icon: 'secured',
      link: OperationsRoutes.apps,
      permissions: OperationsModulePermissions.apps,
    },
    {
      label: 'Feedback',
      icon: 'chat',
      link: OperationsRoutes.feedback,
      permissions: OperationsModulePermissions.feedback,
    },
    {
      label: 'Legal',
      icon: 'clipboard',
      link: OperationsRoutes.legal,
      permissions: OperationsModulePermissions.legal,
    },
    {
      label: 'Flags',
      icon: 'toggle-off',
      link: OperationsRoutes.featureFlags,
      permissions: OperationsModulePermissions.featureFlags,
    },
    {
      label: 'Messages',
      icon: 'mail',
      link: OperationsRoutes.messageTemplates,
      permissions: OperationsModulePermissions.messageTemplates,
    },
    {
      label: 'Observability',
      icon: 'search',
      link: OperationsRoutes.observability,
      permissions: OperationsModulePermissions.observability,
    },
    {
      label: 'Alerts',
      icon: 'mail',
      link: OperationsRoutes.alertRecipients,
      permissions: OperationsModulePermissions.alertRecipients,
    },
    {
      label: 'Affiliates',
      icon: 'group',
      link: OperationsRoutes.affiliates,
      permissions: OperationsModulePermissions.affiliates,
    },
    {
      label: 'Merchants',
      icon: 'briefcase',
      link: OperationsRoutes.merchants,
      permissions: OperationsModulePermissions.merchants,
    },
    {
      label: 'Approvals',
      icon: 'note-approve',
      link: OperationsRoutes.approvals,
      permissions: OperationsModulePermissions.approvals,
    },
  ];

  mobileNavItems: NavItem[] = [];

  ngOnInit(): void {
    this.sessionService
      .getCurrentUserAsync()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (user) {
          this.user = user;
        }

        this.applyAccessibleNavigation(user);
      });
  }

  onSidebarToggle(isCollapsed: boolean): void {
    this.sidebarCollapsed = isCollapsed;
  }

  private applyAccessibleNavigation(
    user: OperationsUserSessionResponse | null,
  ): void {
    this.navItems = this.operationsAccessService.filterNavGroups(
      this.securedNavItems,
      user,
    );

    const mobileItems =
      this.operationsAccessService.filterNavGroups(
        [this.securedMobileNavItems],
        user,
      )[0] ?? [];
    this.mobileNavItems = [
      ...mobileItems,
      {
        label: 'Menu',
        icon: 'menu',
        link: OperationsRoutes.root,
        action: () => this.mobileNav.toggleSideNav(),
      },
    ];

    this.logoLink =
      this.operationsAccessService.firstAccessibleRoute(user) ??
      OperationsRoutes.logout;
  }
}
